import { useCallback, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';

export interface AdminCallNoteItem {
  id: string;
  title: string;
  createdAt: string;
}

export interface AdminCallNotesPageProps {
  careCircleName?: string;
  callNotes: AdminCallNoteItem[];
  isLoading?: boolean;
  onBack?: () => void;
  onAddNew: () => void;
  onView: (callNote: AdminCallNoteItem) => void;
  onEdit: (callNote: AdminCallNoteItem) => void;
  onDelete: (callNote: AdminCallNoteItem) => void;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const AdminCallNotesPage = ({
  careCircleName,
  callNotes,
  isLoading,
  onBack,
  onAddNew,
  onView,
  onEdit,
  onDelete,
}: AdminCallNotesPageProps) => {
  const theme = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<AdminCallNoteItem | null>(
    null,
  );

  const handleDeleteClick = useCallback((note: AdminCallNoteItem) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (noteToDelete) {
      onDelete(noteToDelete);
    }
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  }, [noteToDelete, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  }, []);

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='md'
        headerSlot={
          <PageHeader
            title='Call Notes'
            description={careCircleName ? `For: ${careCircleName}` : undefined}
          />
        }
        contentSlot={
          <Stack spacing={3}>
            <Button
              variant='contained'
              startIcon={<Icon icon='plus' fontSize={18} />}
              onClick={onAddNew}
              size='large'
            >
              Add Call Note
            </Button>

            {isLoading && (
              <Stack
                sx={{
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={32} />
              </Stack>
            )}

            {!isLoading && callNotes.length > 0 && (
              <Stack spacing={2}>
                {callNotes.map((note) => (
                  <Card key={note.id} elevation={2}>
                    <Box sx={{ p: 2 }}>
                      <Stack
                        direction='row'
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Stack
                          direction='row'
                          spacing={1.5}
                          onClick={() => onView(note)}
                          sx={{
                            alignItems: 'center',
                            cursor: 'pointer',
                            flex: 1,
                          }}
                        >
                          <Icon
                            icon='file-lines'
                            fontSize={24}
                            color={theme.palette.primary.main}
                          />
                          <Stack>
                            <Typography
                              variant='subtitle1'
                              sx={{
                                fontWeight: 600,
                              }}
                            >
                              {note.title}
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{
                                color: 'text.secondary',
                              }}
                            >
                              {formatDate(note.createdAt)}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack direction='row' spacing={0.5}>
                          <IconButton
                            size='small'
                            onClick={() => onEdit(note)}
                            aria-label='Edit'
                            color='primary'
                          >
                            <Icon icon='pen' fontSize={18} />
                          </IconButton>
                          <IconButton
                            size='small'
                            onClick={() => handleDeleteClick(note)}
                            aria-label='Delete'
                            color='error'
                          >
                            <Icon icon='trash' fontSize={18} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Box>
                  </Card>
                ))}
              </Stack>
            )}

            {!isLoading && callNotes.length === 0 && (
              <Stack
                spacing={2}
                sx={{
                  alignItems: 'center',
                  py: 6,
                }}
              >
                <Icon
                  icon='file-lines'
                  fontSize={48}
                  color={theme.palette.text.disabled}
                />
                <Typography
                  variant='body1'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No call notes yet
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  Add your first call note to get started
                </Typography>
              </Stack>
            )}

            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
              <DialogTitle>Delete Call Note</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete "{noteToDelete?.title}"? This
                  action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteCancel}>Cancel</Button>
                <Button
                  onClick={handleDeleteConfirm}
                  color='error'
                  variant='contained'
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

AdminCallNotesPage.displayName = 'AdminCallNotesPage';
export default AdminCallNotesPage;
